import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSpendingDemand, useSpendingDemandManager } from '../../../hooks';
import SpendingDemandAPI from '../../../services/SpendingDemandAPI';
import ExtractView from './ExtractView/ExtractView';
import { ExtractField, DEFAULT_EXTRACT_FIELDS, ESSENTIAL_EXTRACT_FIELDS } from '../../../types/spendingDemand.types';
import useQueryURL from '../../../../../utils/useQueryURL';

export default function ExtractPresenter() {
    const { spendingDemandSelected } = useSpendingDemand();
    const { allSpendingDemandList } = useSpendingDemandManager({
        disableQuery: false
    });
    const { getQueryValue } = useQueryURL();
    const orderBy = getQueryValue('spendingdemandmanager-orderBy') || undefined;
    const currentFilter = typeof allSpendingDemandList.currentFilter === 'string'
        ? allSpendingDemandList.currentFilter
        : undefined;
    const querySearch = allSpendingDemandList.sreachValue || undefined;
    const descSearch = allSpendingDemandList.descSearchValue || undefined;

    const [extractProcessId, setExtractProcessId] = useState<string | null>(null);
    const [extractStatus, setExtractStatus] = useState<{
        status: 'PENDING' | 'COMPLETED' | 'ERROR' | 'NOT_FOUND' | null;
        total: number;
        actual: number;
    }>({ status: null, total: 0, actual: 0 });
    const [isExtracting, setIsExtracting] = useState(false);
    // S'assurer que les champs essentiels sont toujours inclus
    const [selectedExtractFields, setSelectedExtractFields] = useState<ExtractField[]>(() => {
        const fields = [...DEFAULT_EXTRACT_FIELDS];
        ESSENTIAL_EXTRACT_FIELDS.forEach(field => {
            if (!fields.includes(field)) {
                fields.push(field);
            }
        });
        return fields;
    });

    // Construire le whereQuery avec les IDs sélectionnés
    const buildWhereQuery = (): string | undefined => {
        if (!spendingDemandSelected || spendingDemandSelected.length === 0) {
            return undefined;
        }
        
        const ids = spendingDemandSelected.map(demand => demand.spending_demand_id);
        const selectedIdsQuery = `spending_demand_id<in>[${ids.join(',')}]`;

        if (!currentFilter || !currentFilter.trim()) {
            return selectedIdsQuery;
        }

        const normalizedCurrentFilter = currentFilter.trim().replace(/\|+$/, '');
        return normalizedCurrentFilter ? `${normalizedCurrentFilter}|${selectedIdsQuery}` : selectedIdsQuery;
    };

    const handleStartExtract = () => {
        startExtract.mutate();
    };

    const handleDownloadExtract = () => {
        if (extractProcessId) {
            downloadExtract.mutate(extractProcessId);
        }
    };

    const handleFieldChange = (field: ExtractField, checked: boolean) => {
        // Ne pas permettre de désélectionner les champs essentiels
        if (!checked && ESSENTIAL_EXTRACT_FIELDS.includes(field)) {
            return;
        }

        setSelectedExtractFields((prevFields) => {
            if (checked) {
                if (prevFields.includes(field)) {
                    return prevFields;
                }
                return [...prevFields, field];
            }
            return prevFields.filter(f => f !== field);
        });
    };

    // Mutation pour démarrer l'extraction
    const startExtract = useMutation({
        mutationFn: async () => {
            setIsExtracting(true);
            setExtractStatus({ status: null, total: 0, actual: 0 });
            
            const whereQuery = buildWhereQuery();
            
            if (!whereQuery) {
                throw new Error('Aucune demande sélectionnée');
            }
            
            try {
                const result = await SpendingDemandAPI.startExtractSpendingDemandsManagerAPI(
                    whereQuery,
                    orderBy || undefined,
                    selectedExtractFields,
                    querySearch || undefined,
                    descSearch || undefined
                );
                
                setExtractProcessId(result.processID);
                return result;
            } catch (error) {
                setIsExtracting(false);
                throw error;
            }
        },
        onError: (error) => {
            setIsExtracting(false);
            setExtractStatus({ status: 'ERROR', total: 0, actual: 0 });
            console.error('Erreur lors de l\'extraction:', error);
        }
    });

    // Mutation pour vérifier le statut
    const checkExtractStatus = useMutation({
        mutationFn: async (processID: string) => {
            return await SpendingDemandAPI.getExtractStatusManagerAPI(processID);
        },
        onSuccess: (data) => {
            setExtractStatus({
                status: data.status as 'PENDING' | 'COMPLETED' | 'ERROR' | 'NOT_FOUND',
                total: data.total,
                actual: data.actual
            });
            if (data.status === 'COMPLETED' || data.status === 'ERROR') {
                setIsExtracting(false);
            }
        },
        onError: (error) => {
            setIsExtracting(false);
            setExtractStatus({ status: 'ERROR', total: 0, actual: 0 });
            console.error('Erreur lors de la vérification du statut:', error);
        }
    });

    // Mutation pour télécharger l'extrait
    const downloadExtract = useMutation({
        mutationFn: async (processID: string) => {
            const response = await SpendingDemandAPI.downloadExtractManagerAPI(processID);
            
            // Créer un blob et télécharger le fichier
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const dateStr = new Date().toISOString().split('T')[0];
            link.download = `spending_demands_extract_${dateStr}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            return response;
        },
        onSuccess: () => {
            // Réinitialiser l'état après téléchargement
            setExtractProcessId(null);
            setExtractStatus({ status: null, total: 0, actual: 0 });
        },
        onError: (error) => {
            console.error('Erreur lors du téléchargement:', error);
        }
    });

    // Polling pour vérifier le statut de l'extraction
    useEffect(() => {
        if (!extractProcessId || !isExtracting) return;

        const interval = setInterval(() => {
            checkExtractStatus.mutate(extractProcessId);
        }, 2000); // Vérifier toutes les 2 secondes

        return () => clearInterval(interval);
    }, [extractProcessId, isExtracting, checkExtractStatus]);

    // Réinitialiser l'état si la sélection change
    useEffect(() => {
        if (!spendingDemandSelected || spendingDemandSelected.length === 0) {
            setExtractProcessId(null);
            setExtractStatus({ status: null, total: 0, actual: 0 });
            setIsExtracting(false);
        }
    }, [spendingDemandSelected]);

    return (
        <ExtractView
            isExtracting={isExtracting}
            extractStatus={extractStatus}
            extractProcessId={extractProcessId}
            selectedExtractFields={selectedExtractFields}
            onStartExtract={handleStartExtract}
            onDownloadExtract={handleDownloadExtract}
            onFieldChange={handleFieldChange}
            isLoading={startExtract.isPending || downloadExtract.isPending}
            selectedCount={spendingDemandSelected?.length || 0}
        />
    );
}

